import gql from 'graphql-tag';
import { UserFragment } from '@core/api/user.fragment';
import {
	CommentFragment,
	PageInfoFragment,
} from '@app/pages/cmms/graphql/cmms-fragments';
import { SimpleLabelFragment } from '@app/pages/settings/graphql/labels.queries';

export const IncidentFragment = gql`
	fragment IncidentFragment on IncidentType {
		pk
		assets {
			id
			pk
			name
			location {
				id
				name
				type
			}
		}
		coordinates {
			x
			y
			z
		}
		created
		createdBy {
			...UserFragment
		}
		occurredAt
		completedAt
		incidentId
		name
		id
		pk
		priority
		locations {
			id
			locationId
			locationType
			name
			pk
		}
		category
		description
		status
		updated
		assignee {
			...UserFragment
		}
		approvedBy {
			...UserFragment
		}
		files {
			pk
			file {
				contentType
				name
				url
			}
			classificationsV2 {
				name
				type
				pk
			}
			createdBy {
				name
				lastName
				smallAvatarUrl
			}
			created
			type
			name
			importConfirmed
			importId
		}
		workOrders {
			workOrderId
			status
			id
			pk
			name
			workOrderId
			priority
			locations {
				id
				locationId
				locationType
				name
				pk
			}
		}
		requestIncident(first: 100) {
			pageInfo {
				...PageInfoFragment
			}
			edges {
				cursor
				node {
					priority
					id
					pk
					name
					status
					requestId
				}
			}
		}
		comments {
			...CommentFragment
		}
		costAllocation
		costOfRepair
		deviationType
		riskLevel
		consequenceRating
		timeframe
		deadline
		labels {
			...SimpleLabelFragment
		}
	}
	${PageInfoFragment}
	${CommentFragment}
	${UserFragment}
	${SimpleLabelFragment}
`;

export const PaginatedIncidentsFragment = gql`
	fragment PaginatedIncidentsFragment on IncidentTypeConnection {
		pageInfo {
			...PageInfoFragment
		}
		edges {
			cursor
			node {
				...IncidentFragment
			}
		}
		totalCount
	}
	${PageInfoFragment}
	${IncidentFragment}
`;

export const IncidentsExportFragment = gql`
	fragment IncidentsExportFragment on IncidentTypeConnection {
		exportUrl
	}
`;

export const CalendarIncidentFragment = gql`
	fragment CalendarIncidentFragment on IncidentTypeConnection {
		edges {
			node {
				pk
				name
				occurredAt
				completedAt
				created
				status
			}
		}
	}
`;

export const IncidentsQuery = gql`
	query IncidentQuery(
		$first: Int
		$last: Int
		$after: String
		$before: String
		$assignee: [String]
		$name: String
		$description: String
		$incidentId: [BigInt]
		$status: [String]
		$priority: [Int]
		$sortBy: String
		$search: String
		$category: [String]
		$locations: [UUID]
		$exportColumns: [String]
		$calendarAfter: DateTime
		$calendarBefore: DateTime
		$isCalendarQuery: Boolean = false
		$isExportQuery: Boolean = false
		$isFullPaginatedFragmentQuery: Boolean = true
		$costAllocation: [String]
		$deviationType: [String]
		$riskLevel: [String]
		$timeframe: [String]
		$consequenceRating: [String]
		$costOfRepair: Decimal
		$costOfRepair_Gt: Decimal
		$costOfRepair_Gte: Decimal
		$costOfRepair_Isnull: Boolean
		$costOfRepair_Lt: Decimal
		$costOfRepair_Lte: Decimal
		$deadlineAfter: DateTime
		$deadlineBefore: DateTime
		$labels: [ID]
	) {
		incidents(
			first: $first
			after: $after
			before: $before
			last: $last
			assignee: $assignee
			name: $name
			description: $description
			incidentId_In: $incidentId
			status: $status
			priority_In: $priority
			sortBy: $sortBy
			search: $search
			category: $category
			locations: $locations
			exportColumns: $exportColumns
			calendarAfter: $calendarAfter
			calendarBefore: $calendarBefore
			costAllocation: $costAllocation
			deviationType: $deviationType
			riskLevel: $riskLevel
			consequenceRating: $consequenceRating
			timeframe: $timeframe
			costOfRepair: $costOfRepair
			costOfRepair_Gt: $costOfRepair_Gt
			costOfRepair_Gte: $costOfRepair_Gte
			costOfRepair_Isnull: $costOfRepair_Isnull
			costOfRepair_Lt: $costOfRepair_Lt
			costOfRepair_Lte: $costOfRepair_Lte
			deadline_Gte: $deadlineAfter
			deadline_Lte: $deadlineBefore
			labels_Id_In: $labels
		) {
			...PaginatedIncidentsFragment @include(if: $isFullPaginatedFragmentQuery)
			...IncidentsExportFragment @include(if: $isExportQuery)
			...CalendarIncidentFragment @include(if: $isCalendarQuery)
		}
	}
	${PaginatedIncidentsFragment}
	${IncidentsExportFragment}
	${CalendarIncidentFragment}
`;

export const GetIncidentQuery = gql`
	query GetIncidentQuery($id: UUID) {
		incident(id: $id) {
			...IncidentFragment
		}
	}
	${IncidentFragment}
`;

export const IncidentsSearchQuery = gql`
	query IncidentsSearchQuery(
		$search: String
		$first: Int
		$last: Int
		$after: String
		$before: String
	) {
		incidents(
			search: $search
			first: $first
			last: $last
			after: $after
			before: $before
		) {
			pageInfo {
				...PageInfoFragment
			}
			edges {
				cursor
				node {
					...IncidentFragment
				}
			}
			totalCount
		}
	}
	${IncidentFragment}
	${PageInfoFragment}
`;
